import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { 
  KeyRound, 
  Mail, 
  Lock, 
  ArrowRight, 
  Eye, 
  EyeOff,
  Shield,
  Zap,
  CheckCircle,
  Loader2
} from 'lucide-react';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    // Check for remembered email
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/auth/login', formData);
      const { access_token, user } = response.data;
      
      // Handle remember me
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      login(access_token, user);
      toast.success('Welcome back!', {
        description: `Hello ${user.full_name}, accessing your panel...`,
        duration: 3000,
      });
      
      // Small delay for better UX with role-based navigation
      setTimeout(() => {
        if (user.role === 'superadmin') {
          navigate('/superadmin');
        } else {
          navigate('/dashboard');
        }
      }, 1500);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error('Authentication Error', {
        description: message,
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.email && formData.password;

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-slate-800/30"></div>
      
      {/* Floating Elements */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className={`w-full max-w-md transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          
          {/* Logo and Header */}
          <div className="text-center mb-8 space-y-4">
            <div className="inline-flex items-center justify-center p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-2xl shadow-blue-500/25 mb-6">
              <KeyRound className="h-12 w-12 text-white drop-shadow-lg" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                APT-AccessHub
              </h1>
              <p className="text-blue-200/80 text-lg">
                Your centralized access control
              </p>
            </div>
            
            {/* Feature highlights */}
            <div className="flex justify-center space-x-6 mt-6">
              <div className="flex items-center space-x-2 text-blue-300/70">
                <Shield className="h-4 w-4" />
                <span className="text-sm">Secure</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300/70">
                <Zap className="h-4 w-4" />
                <span className="text-sm">Fast</span>
              </div>
              <div className="flex items-center space-x-2 text-blue-300/70">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Reliable</span>
              </div>
            </div>
          </div>

          {/* Login Form */}
          <Card className="backdrop-blur-xl bg-white/10 border-white/20 shadow-2xl shadow-black/20">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-semibold text-white">
                Sign In
              </CardTitle>
              <p className="text-blue-200/70 text-sm mt-2">
                Access your enterprise control panel
              </p>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/90 font-medium">
                    Email Address
                  </Label>
                  <div className="relative group">
                    <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'email' ? 'text-blue-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-blue-400 focus:ring-blue-400/20 transition-all duration-200"
                      placeholder="you@company.com"
                      required
                    />
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300 ${
                      focusedField === 'email' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white/90 font-medium">
                    Password
                  </Label>
                  <div className="relative group">
                    <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 transition-colors duration-200 ${
                      focusedField === 'password' ? 'text-blue-400' : 'text-white/50'
                    }`} />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      className="pl-10 pr-10 h-12 bg-white/10 backdrop-blur-sm border-white/20 text-white placeholder:text-white/50 focus:border-blue-400 focus:ring-blue-400/20 transition-all duration-200"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors duration-200"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                    <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300 ${
                      focusedField === 'password' ? 'w-full' : 'w-0'
                    }`}></div>
                  </div>
                </div>

                {/* Remember Me */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="rounded border-white/20 bg-white/10 text-blue-400 focus:ring-blue-400 focus:ring-offset-0"
                    />
                    <span className="text-white/80 text-sm">Remember email</span>
                  </label>
                  
                  <Link
                    to="/forgot-password"
                    className="text-blue-300 hover:text-blue-200 text-sm transition-colors duration-200"
                  >
                    Forgot your password?
                  </Link>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={!isFormValid || loading}
                  className={`w-full h-12 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg transition-all duration-300 transform ${
                    !loading && isFormValid ? 'hover:scale-105 hover:shadow-xl hover:shadow-blue-500/25' : ''
                  } ${!isFormValid ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Sign In</span>
                      <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                    </div>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-transparent text-white/60">First time here?</span>
                </div>
              </div>

              {/* Register Link */}
              <div className="text-center">
                <p className="text-white/70 text-sm mb-3">
                  Join thousands of professionals already using APT-AccessHub
                </p>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center w-full h-11 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-medium rounded-xl hover:bg-white/20 transition-all duration-300 space-x-2"
                >
                  <span>Create Free Account</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Trust Indicators */}
          <div className="mt-8 text-center">
            <div className="flex justify-center items-center space-x-4 text-blue-300/60 text-xs mb-4">
              <div className="flex items-center space-x-1">
                <Shield className="h-3 w-3" />
                <span>AES-256 Encryption</span>
              </div>
              <div className="w-1 h-1 bg-blue-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <CheckCircle className="h-3 w-3" />
                <span>SSL Secure</span>
              </div>
              <div className="w-1 h-1 bg-blue-300/40 rounded-full"></div>
              <div className="flex items-center space-x-1">
                <Zap className="h-3 w-3" />
                <span>99.9% Uptime</span>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="text-blue-300/50 text-xs">
              Powered by <span className="font-medium text-blue-300/70">Budget Digital Solutions LLC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;